import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Check } from 'lucide-react'
import { Icon } from './ui/icon'
import { useModule } from '../contexts/ModuleContext'

export function ModuleSwitcher() {
  const { t } = useTranslation()
  const { currentModule, setModule, modules } = useModule()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (id: string) => {
    setOpen(false)
    if (id !== currentModule.id) {
      setModule(id)
    }
  }

  return (
    <div className="ad-modsw" ref={ref}>
      <button
        type="button"
        className="ad-modsw__trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ad-modsw__tile" style={{ background: currentModule.accent }}>
          <Icon name={currentModule.icon} className="size-4" />
        </span>
        <span className="ad-modsw__name">{currentModule.label}</span>
        <ChevronDown className="size-3 ad-modsw__chev" />
      </button>

      {open && (
        <div className="module-menu" role="menu">
          <div className="module-menu__head">{t('nav.switchModule')}</div>
          {modules.map((m) => {
            const active = m.id === currentModule.id
            return (
              <button
                key={m.id}
                type="button"
                role="menuitem"
                className={`module-menu__item${active ? ' is-active' : ''}`}
                onClick={() => pick(m.id)}
              >
                <span className="module-menu__tile" style={{ background: m.accent }}>
                  <Icon name={m.icon} className="size-4" />
                </span>
                <span className="module-menu__text">
                  <span className="module-menu__name">{m.label}</span>
                  <span className="module-menu__blurb">{m.blurb}</span>
                </span>
                {active && <Check className="size-4 module-menu__check" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
